import { Navigate, Outlet, useParams } from 'react-router';
import { LoadingScreen } from '@/components/base';
import { useAuthContext } from '@/contexts/authContext';
import { useUserWebappRoutesAccess } from '@/hooks/userResourceAccess/useUserWebappRoutesAccess';
import { ActionType, LoadingLogData } from '@/interfaces/logging';
import { authenticatedRoutes } from '@/router/routes';

export const StudentAppSessionRouteGuard = () => {
  const params = useParams();

  const { user } = useAuthContext();
  const { isLoading, data, error } = useUserWebappRoutesAccess(user?.id, {
    classroomId: params.classroomId,
    lessonInstanceId: params.lessonInstanceId,
    activityInstanceId: params.activityInstanceId,
  });

  // Data is still loading
  if (isLoading) {
    const loadingLogData: LoadingLogData = {
      action: ActionType.OnPageLoad,
      component: 'StudentAppSessionRouteGuard',
      description: 'Loading user webapp routes access',
      context: { params, isLoading, error },
    };
    return <LoadingScreen loadingLogData={loadingLogData} />;
  }

  // Error getting access data from BE
  if (error) {
    return <Navigate to={authenticatedRoutes.Classrooms.getRoute()} />;
  }

  /**
   * There is an issue for swr 👉🏻 https://github.com/vercel/swr/issues/4112
   *
   * When using immutable and new key is supplied, the `data` becomes `undefined` but `isLaoding` is still `false`
   * It causes the state to be `{ data: undefined, isLoading: false }` which is unexpected.
   *
   * In this buggy gap, we render the loading screen (as it, in fact, should already be the loading stage)
   */
  if (!data) {
    const loadingLogData: LoadingLogData = {
      action: ActionType.OnPageLoad,
      component: 'StudentAppSessionRouteGuard',
      description: 'Waiting for SWR data (workaround for SWR bug)',
      context: { params, isLoading, data, error },
    };
    return <LoadingScreen loadingLogData={loadingLogData} />;
  }

  // user cannot access this route by the given params
  if (!data.hasAccess) {
    return <Navigate to={authenticatedRoutes.Classrooms.getRoute()} />;
  }

  return <Outlet />;
};
